import {
  type InvitationFailureReason,
  type InvitationOutcome,
  type LimitOverrides,
  type OperatorTenantDetail,
  type Plan,
  type PlanLimitKey,
  readDomainSignInMode,
  renameTenantRequestSchema,
  TENANT_ID_PREFIX_LENGTH,
  tenantIdPrefix,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { LifeBuoy } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { formatBytes, formatNumber, formatUsd } from "@/components/charts";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader, SectionHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../api.ts";
import { ApiError, apiError, needsReauth, ok } from "../api-errors.ts";
import { formatDate, formatDateTime } from "../i18n/format.ts";
import {
  CONSOLE_PATH,
  consoleTenantPath,
  Link,
  navigate,
  TENANT_TABS,
  type TenantTab,
} from "../router.tsx";
import { StepUpForm, StepUpFrame, useSecureAction } from "../security/step-up.tsx";
import { SettingsGroupTabs } from "../settings-shell/SettingsGroupTabs.tsx";
import { useCsrfHeaders } from "./operator.tsx";
import { useOperatorStepUp } from "./operator-step-up.ts";
import {
  consoleRefusal,
  limitText,
  spendLevel,
  spendText,
  TenantStatus,
  UsageMeter,
} from "./parts.tsx";
import { consoleKeys, usePlans, useTenant } from "./queries.ts";
import { RequestSupportDialog } from "./SupportPage.tsx";

const GB = 1024 ** 3;

/** How each limit is entered and shown: storage in GB, spend in USD. */
export const LIMIT_FIELDS: {
  key: PlanLimitKey;
  toInput: (v: number) => string;
  fromInput: (s: string) => number;
  format: (v: number) => string;
  step: string;
}[] = [
  { key: "seats", toInput: String, fromInput: Number, format: (v) => formatNumber(v), step: "1" },
  {
    key: "storageBytes",
    toInput: (v) => String(Math.round((v / GB) * 100) / 100),
    fromInput: (s) => Math.round(Number(s) * GB),
    format: formatBytes,
    step: "0.01",
  },
  {
    key: "ocrMonthlyPages",
    toInput: String,
    fromInput: Number,
    format: (v) => formatNumber(v),
    step: "1",
  },
  { key: "modelMonthlyUsd", toInput: String, fromInput: Number, format: formatUsd, step: "0.01" },
];

/** A deleted tenant keeps its record only: no limits or lifecycle to act on. */
function tabsOf(status: OperatorTenantDetail["status"]): readonly TenantTab[] {
  return TENANT_TABS.filter((g) => status !== "deleted" || (g !== "limits" && g !== "lifecycle"));
}

/**
 * The operator view of one tenant (spec "Operator view of a tenant's administration"): status,
 * plan and limits, its tenant admins (their contract counterparts), identity providers, verified
 * domains and counts. Never an entity, project or any other user. One page per tab (spec "Tenant
 * detail pages per tab"); a deleted tenant keeps Overview, Admins and Sign-in only.
 */
export function TenantDetail({ tenantId, tab }: { tenantId: string; tab: TenantTab }) {
  const { t } = useTranslation();
  const tenant = useTenant(tenantId);
  const [requesting, setRequesting] = useState(false);
  const status = tenant.data?.status;
  useEffect(() => {
    // A tab this tenant does not have: its address becomes Overview's, in place.
    if (status && !tabsOf(status).includes(tab)) {
      navigate(consoleTenantPath(tenantId), { replace: true });
    }
  }, [status, tab, tenantId]);
  const back = (
    <nav aria-label={t("project.breadcrumb")} className="text-sm">
      <Link
        href={CONSOLE_PATH}
        className="text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
      >
        {t("console.tenants.back")}
      </Link>
    </nav>
  );
  if (tenant.isError) {
    return (
      <Page>
        {back}
        <Notice tone="danger">{tenant.error.message}</Notice>
      </Page>
    );
  }
  if (!tenant.data) {
    return (
      <Page>
        {back}
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }
  const data = tenant.data;
  const tabs = tabsOf(data.status);
  const current = tabs.includes(tab) ? tab : "overview";
  return (
    <Page>
      <PageHeader
        before={back}
        title={data.name}
        lead={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-secondary">
            <TenantStatus status={data.status} />
            <span aria-hidden="true">·</span>
            <span>{data.plan.name}</span>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              {t("console.tenants.createdOn", { date: formatDate(data.createdAt) })}
            </span>
          </span>
        }
        actions={
          data.status === "deleted" ? null : (
            <Button variant="outline" onClick={() => setRequesting(true)}>
              <LifeBuoy aria-hidden="true" />
              {t("console.support.request")}
            </Button>
          )
        }
      />
      <SettingsGroupTabs
        label={t("console.tenantTabs.label")}
        groups={tabs}
        current={current}
        href={(g) => consoleTenantPath(data.id, g)}
        groupLabel={(g) => t(`console.tenantTabs.${g}`)}
        count={(g) => (g === "admins" ? data.admins.length : 0)}
        countLabel={(_, count) => t("console.tenants.adminCount", { count })}
      />
      {current === "overview" ? <Overview tenant={data} /> : null}
      {current === "admins" ? <Admins tenant={data} /> : null}
      {current === "sign-in" ? <Federation tenant={data} /> : null}
      {current === "limits" ? <LimitsEditor tenant={data} /> : null}
      {current === "lifecycle" ? <Lifecycle tenant={data} /> : null}
      {requesting ? (
        <RequestSupportDialog tenantId={data.id} onClose={() => setRequesting(false)} />
      ) : null}
    </Page>
  );
}

function Overview({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  return (
    <>
      <InvitationWarnings tenant={tenant} />
      {/* Outside the list: a notice is neither a term nor its value. */}
      {tenant.modelSpend?.alert ? (
        <Notice tone={tenant.modelSpend.alert === "reached" ? "danger" : "warning"}>
          {t(`console.spendNotice.${tenant.modelSpend.alert}.${tenant.modelSpend.mode}`)}
        </Notice>
      ) : null}
      <OverviewFacts tenant={tenant} />
    </>
  );
}

type TenantAdmin = OperatorTenantDetail["admins"][number];

/** Why an invitation e-mail did not go out, in the operator's words. */
function reasonText(t: TFunction, reason: InvitationFailureReason | null | undefined) {
  return t(`console.adminInvitation.reason.${reason ?? "unknown"}`);
}

/** An admin whose invitation needs the operator: it failed, or expired unaccepted. */
const stuck = (admin: TenantAdmin) =>
  admin.invitation?.state === "failed" || admin.invitation?.state === "expired";

/**
 * A persistent warning per admin whose invitation failed or expired (spec "Invitation state in
 * the operator view"), from the server's view of the tenant, so it survives a reload. Resend
 * while the tenant is active.
 */
function InvitationWarnings({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  // Resent from here: the refreshed view drops the warning, so its confirmation stays here.
  const [resent, setResent] = useState<string[]>([]);
  const admins = tenant.admins.filter(stuck);
  const confirmed = resent.filter((email) => !admins.some((a) => a.email === email));
  if ((admins.length === 0 && confirmed.length === 0) || tenant.status === "deleted") {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      {confirmed.map((email) => (
        <Notice key={email} tone="success" live>
          {t("console.adminInvitation.resent", { email })}
        </Notice>
      ))}
      {admins.map((admin) => (
        <Notice
          key={admin.email}
          tone="warning"
          action={
            admin.id && tenant.status === "active" ? (
              <ResendInvitation
                tenantId={tenant.id}
                admin={admin}
                onSent={() => setResent((r) => [...r, admin.email])}
              />
            ) : undefined
          }
        >
          {admin.invitation?.state === "failed"
            ? t("console.adminInvitation.failed", {
                email: admin.email,
                reason: reasonText(t, admin.invitation.reason),
              })
            : t("console.adminInvitation.expired", {
                email: admin.email,
                date: formatDateTime(admin.invitation?.at ?? ""),
              })}
        </Notice>
      ))}
    </div>
  );
}

/** One line under an admin: where their invitation stands, if it is not accepted yet. */
function invitationLine(t: TFunction, admin: TenantAdmin): string | null {
  const invitation = admin.invitation;
  if (!invitation) {
    return null;
  }
  if (invitation.state === "failed") {
    return t("console.adminInvitation.failedShort", { reason: reasonText(t, invitation.reason) });
  }
  if (invitation.state === "expired") {
    return t("console.adminInvitation.expiredShort", {
      date: formatDate(invitation.at ?? ""),
    });
  }
  return invitation.at
    ? t("console.adminInvitation.sentShort", { date: formatDate(invitation.at) })
    : t("console.adminInvitation.pendingShort");
}

/**
 * Resends an admin's invitation (operator Resend): the outcome in words beside the button, and
 * the tenant's view refreshed so the warning follows. Never shows or copies a link.
 */
function ResendInvitation({
  tenantId,
  admin,
  onSent,
}: {
  tenantId: string;
  admin: TenantAdmin;
  /** Called once the e-mail went out, before the tenant's view is refreshed. */
  onSent?: () => void;
}) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const resend = useMutation({
    mutationFn: async () => {
      const res = await api.api.operator.tenants[":tenantId"].admins[":userId"].invitation.$post(
        { param: { tenantId, userId: admin.id as string } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
      return (await res.json()) as InvitationOutcome;
    },
    onSuccess: (outcome) => {
      if (outcome.sent) {
        onSent?.();
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenantId) }),
  });
  return (
    <div className="flex flex-col gap-1.5">
      <Button
        variant="outline"
        size="sm"
        className="self-start"
        aria-label={t("console.adminInvitation.resendOf", { name: admin.name ?? admin.email })}
        disabled={resend.isPending}
        onClick={() => resend.mutate()}
      >
        {t("console.adminInvitation.resend")}
      </Button>
      {resend.data?.sent ? (
        <p role="status" className="text-sm text-fg-secondary">
          {t("console.adminInvitation.resent", { email: admin.email })}
        </p>
      ) : null}
      {resend.data && !resend.data.sent ? (
        <InlineError>
          {t("console.adminInvitation.notResent", {
            email: admin.email,
            reason: reasonText(t, resend.data.reason),
          })}
        </InlineError>
      ) : null}
      {resend.error ? <InlineError>{consoleRefusal(resend.error)}</InlineError> : null}
    </div>
  );
}

/** A scheduled deletion and its purge date (Overview, and Lifecycle beside its controls). */
function DeletionNotice({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  return tenant.deletionDueAt ? (
    <Notice tone="warning">
      {t("console.lifecycle.scheduledNotice", { date: formatDateTime(tenant.deletionDueAt) })}
    </Notice>
  ) : null;
}

/** Use against limits, one hairline row each, with the synthetic demo spend as a muted line. */
function OverviewFacts({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const spend = tenant.modelSpend;
  const rows: { label: string; value: string; meter?: ReactNode; note?: string }[] = [
    {
      label: t("console.tenants.users"),
      value: `${formatNumber(tenant.userCount)} / ${limitText(tenant.limits.effective.seats)}`,
      meter: (
        <UsageMeter
          used={tenant.userCount}
          limit={tenant.limits.effective.seats}
          label={t("console.tenants.users")}
        />
      ),
    },
    {
      label: t("console.tenants.ocr"),
      value: `${formatNumber(tenant.ocrBudget.usedThisMonth)} / ${limitText(tenant.ocrBudget.monthlyPages)}`,
      meter: (
        <UsageMeter
          used={tenant.ocrBudget.usedThisMonth}
          limit={tenant.ocrBudget.monthlyPages}
          label={t("console.tenants.ocr")}
        />
      ),
    },
    {
      label: t("console.tenants.spend"),
      value: spendText(spend),
      meter: spend ? (
        <UsageMeter
          used={spend.usedThisMonth}
          limit={spend.monthlyUsd}
          label={t("console.tenants.spend")}
          level={spendLevel(spend)}
        />
      ) : undefined,
      note: spend?.syntheticUsd
        ? t("console.syntheticSpendDetail", { amount: formatUsd(spend.syntheticUsd) })
        : undefined,
    },
    { label: t("console.tenants.entities"), value: formatNumber(tenant.entityCount) },
  ];
  return (
    <section className="flex flex-col gap-3">
      <dl className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
        <TenantNameEditor key={tenant.id} tenant={tenant} />
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[14rem_1fr] sm:gap-6"
          >
            <dt className="text-fg-secondary">{row.label}</dt>
            <dd className="flex max-w-md flex-col">
              <span className="font-medium text-fg tabular-nums">{row.value}</span>
              {row.meter}
              {row.note ? <span className="mt-1 text-xs text-fg-muted">{row.note}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      <DeletionNotice tenant={tenant} />
    </section>
  );
}

function TenantNameEditor({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tenant.name);
  const editButton = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const finish = () => {
    setEditing(false);
    requestAnimationFrame(() => editButton.current?.focus());
  };
  useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    }
  }, [editing]);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.operator.tenants[":tenantId"].$patch(
          { param: { tenantId: tenant.id }, json: { name: name.trim() } },
          headers,
        ),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenants }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.modelConnections }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.support }),
      ]);
      finish();
    },
  });
  const valid =
    renameTenantRequestSchema.safeParse({ name }).success && name.trim() !== tenant.name;
  return (
    <div className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[14rem_1fr] sm:gap-6">
      <dt className="text-fg-secondary">
        {editing ? (
          <Label htmlFor={id}>{t("console.tenants.name")}</Label>
        ) : (
          t("console.tenants.name")
        )}
      </dt>
      <dd className="min-w-0">
        {editing ? (
          <form
            className="flex max-w-md flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!save.isPending && valid) {
                save.mutate();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !save.isPending) {
                event.preventDefault();
                finish();
              }
            }}
          >
            <Input
              ref={input}
              id={id}
              value={name}
              maxLength={200}
              required
              disabled={save.isPending}
              onChange={(event) => setName(event.target.value)}
            />
            {save.error ? <InlineError>{consoleRefusal(save.error)}</InlineError> : null}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm" disabled={save.isPending || !valid}>
                {t(save.isPending ? "app.saving" : "app.save")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={save.isPending}
                onClick={finish}
              >
                {t("app.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-0 break-words font-medium">{tenant.name}</span>
            {tenant.status !== "deleted" && tenant.status !== "deletion_scheduled" ? (
              <Button
                ref={editButton}
                size="sm"
                variant="ghost"
                onClick={() => {
                  setName(tenant.name);
                  save.reset();
                  setEditing(true);
                }}
              >
                {t("console.tenants.rename")}
              </Button>
            ) : null}
          </div>
        )}
      </dd>
    </div>
  );
}

function Admins({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const id = useId();
  const [resetting, setResetting] = useState<TenantAdmin | null>(null);
  return (
    <section aria-labelledby={`${id}-admins`} className="flex flex-col gap-3">
      <SectionHeader id={`${id}-admins`} title={t("console.tenants.admins")} />
      <p className="text-sm text-fg-secondary">{t("console.tenants.adminsNote")}</p>
      {tenant.admins.length === 0 ? (
        <Notice tone="warning">{t("console.tenants.noAdmins")}</Notice>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {tenant.admins.map((admin) => (
            <li key={admin.email} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <span className="flex min-w-0 flex-1 basis-48 flex-col">
                <span className="truncate text-sm font-medium text-fg">
                  {admin.name ?? admin.email}
                </span>
                {admin.name ? (
                  <span className="truncate text-xs text-fg-muted">{admin.email}</span>
                ) : null}
                {invitationLine(t, admin) ? (
                  <span
                    className={
                      stuck(admin) ? "text-xs text-warning-fg" : "text-xs text-fg-secondary"
                    }
                  >
                    {invitationLine(t, admin)}
                  </span>
                ) : null}
              </span>
              {/* Only a failed or expired invitation: a resend voids a delivered, pending link. */}
              {stuck(admin) && admin.id && tenant.status === "active" ? (
                <ResendInvitation tenantId={tenant.id} admin={admin} />
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("console.mfa.resetOf", { name: admin.name ?? admin.email })}
                onClick={() => setResetting(admin)}
              >
                {t("console.mfa.reset")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {resetting ? (
        <AdminMfaDialog
          tenantId={tenant.id}
          admin={resetting}
          canResend={tenant.status === "active"}
          onClose={() => setResetting(null)}
        />
      ) : null}
    </section>
  );
}

function AdminMfaDialog(props: {
  tenantId: string;
  admin: TenantAdmin;
  canResend: boolean;
  onClose: () => void;
}) {
  const email = props.admin.email;
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const [reason, setReason] = useState("");
  const reset = useMutation({
    mutationFn: async () => {
      const res = await api.api.operator.tenants[":tenantId"]["admin-mfa-reset"].$post(
        {
          param: { tenantId: props.tenantId },
          json: { email, reason: reason.trim() },
        },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
  });
  return (
    <AppDialog
      open
      title={t("console.mfa.title", { email })}
      description={t("console.mfa.body")}
      onClose={props.onClose}
    >
      {reset.error instanceof ApiError && reset.error.code === "invitation_pending" ? (
        // Nothing to reset for an admin who never signed in: a resend is what helps (D8).
        <div className="flex flex-col gap-4">
          <Notice
            tone="warning"
            action={
              props.canResend && props.admin.id ? (
                <ResendInvitation tenantId={props.tenantId} admin={props.admin} />
              ) : undefined
            }
          >
            {t("console.mfa.invitationPending", { email })}
          </Notice>
          <Button variant="ghost" className="self-end" onClick={props.onClose}>
            {t("app.close")}
          </Button>
        </div>
      ) : reset.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Notice tone="success" live>
            {t("console.mfa.done")}
          </Notice>
          <Button className="self-end" onClick={props.onClose}>
            {t("app.close")}
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim() && !reset.isPending) {
              reset.mutate();
            }
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-reason`}>{t("console.mfa.reason")}</Label>
            <Textarea
              id={`${id}-reason`}
              required
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </div>
          {reset.error ? <Notice tone="danger">{consoleRefusal(reset.error)}</Notice> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={props.onClose}>
              {t("app.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!reason.trim() || reset.isPending}
            >
              {t("console.mfa.confirm")}
            </Button>
          </div>
        </form>
      )}
    </AppDialog>
  );
}

function Federation({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <section aria-labelledby={`${id}-fed`} className="grid gap-6 sm:grid-cols-2">
      <div className="flex flex-col gap-3">
        <h2 id={`${id}-fed`} className="text-sm font-semibold text-fg">
          {t("console.tenants.idps")}
        </h2>
        {tenant.identityProviders.length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("console.tenants.noIdps")}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {tenant.identityProviders.map((p) => (
              <li key={`${p.protocol}-${p.displayName}`} className="text-fg">
                {p.displayName}
                <span className="text-fg-muted">
                  {` · ${p.protocol.toUpperCase()} · ${t(`console.tenants.idpStatus.${p.status}`)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-fg">{t("console.tenants.domains")}</h2>
        {tenant.domains.length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("console.tenants.noDomains")}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-fg">
            {tenant.domains.map((d) => {
              // Bound to a live IdP: its IdP and mode (#419, D13, read-only; N-1 sends none).
              const signIn = tenant.domainSignIn?.find((s) => s.domain === d);
              return (
                <li key={d}>
                  {d}
                  {signIn ? (
                    <span className="text-fg-muted">
                      {/* A previous API may still send the retired `sso_only` (#438). */}
                      {` · ${signIn.identityProvider} · ${t(`signin.mode.labels.${readDomainSignInMode(signIn.effectiveSignInMode)}`)}`}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

/** The plan and each override (empty follows the plan); audited in both logs by the server. */
function LimitsEditor({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const plans = usePlans();
  const queryClient = useQueryClient();
  const [planId, setPlanId] = useState(tenant.limits.plan.id);
  const [inputs, setInputs] = useState<Record<PlanLimitKey, string>>(
    () =>
      Object.fromEntries(
        LIMIT_FIELDS.map((f) => {
          const v = tenant.limits.overrides[f.key];
          return [f.key, v === null ? "" : f.toInput(v)];
        }),
      ) as Record<PlanLimitKey, string>,
  );
  const plan: Plan | undefined = plans.data?.find((p) => p.id === planId);
  const overrides = Object.fromEntries(
    LIMIT_FIELDS.map((f) => [
      f.key,
      inputs[f.key].trim() === "" ? null : f.fromInput(inputs[f.key]),
    ]),
  ) as LimitOverrides;
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.operator.tenants[":tenantId"].limits.$put(
          { param: { tenantId: tenant.id }, json: { planId, overrides } },
          headers,
        ),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenant.id) }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenants }),
      ]),
  });
  return (
    <section aria-labelledby={`${id}-limits`} className="flex flex-col gap-3">
      <SectionHeader id={`${id}-limits`} title={t("console.limits.title")} />
      <form
        className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!save.isPending) {
            save.mutate();
          }
        }}
      >
        <div className="flex max-w-xs flex-col gap-1.5">
          <Label htmlFor={`${id}-plan`}>{t("console.tenants.plan")}</Label>
          <NativeSelect
            id={`${id}-plan`}
            value={planId}
            onChange={(e) => {
              save.reset();
              setPlanId(e.currentTarget.value);
            }}
          >
            {(plans.data ?? [{ id: tenant.limits.plan.id, name: tenant.limits.plan.name }]).map(
              (p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ),
            )}
          </NativeSelect>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {LIMIT_FIELDS.map((f) => {
            const fromPlan = plan?.limits[f.key] ?? null;
            return (
              <div key={f.key} className="flex flex-col gap-1.5">
                <Label htmlFor={`${id}-${f.key}`}>{t(`console.limits.${f.key}`)}</Label>
                <Input
                  id={`${id}-${f.key}`}
                  type="number"
                  min={0}
                  step={f.step}
                  inputMode="decimal"
                  aria-describedby={`${id}-${f.key}-hint`}
                  value={inputs[f.key]}
                  onChange={(e) => {
                    save.reset();
                    setInputs({ ...inputs, [f.key]: e.currentTarget.value });
                  }}
                />
                <span id={`${id}-${f.key}-hint`} className="text-xs text-fg-muted">
                  {t("console.limits.fromPlan", { value: limitText(fromPlan, f.format) })}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-fg-muted">{t("console.limits.note")}</p>
        {save.error ? <InlineError>{consoleRefusal(save.error)}</InlineError> : null}
        {save.isSuccess ? (
          <Notice tone="success" live>
            {t("console.limits.saved")}
          </Notice>
        ) : null}
        <Button type="submit" className="self-start" disabled={save.isPending}>
          {t("app.save")}
        </Button>
      </form>
    </section>
  );
}

type LifecycleAction = "suspend" | "reactivate" | "delete" | "cancel";

/**
 * Suspend, reactivate, schedule deletion (the exact name typed), cancel it, and delete now
 * (tenant-deletion-controls: the exact name, the ID's first 8 characters and a step-up).
 */
function Lifecycle({ tenant }: { tenant: OperatorTenantDetail }) {
  const { t } = useTranslation();
  const id = useId();
  const [action, setAction] = useState<LifecycleAction | null>(null);
  const [expediting, setExpediting] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  // Once due (expedited, or the grace period over) the purge has started: nothing to cancel or
  // expedite, the server would refuse both. Due on the server's clock (#404); the browser's only
  // for an older API revision that does not say.
  const purging =
    tenant.status === "deletion_scheduled" &&
    (tenant.deletionDue ??
      (tenant.deletionDueAt !== null && Date.parse(tenant.deletionDueAt) <= Date.now()));
  const scheduled = tenant.status === "deletion_scheduled" && !purging;
  const available: LifecycleAction[] =
    tenant.status === "active"
      ? ["suspend", "delete"]
      : tenant.status === "suspended"
        ? ["reactivate", "delete"]
        : scheduled
          ? ["cancel"]
          : [];
  return (
    <section aria-labelledby={`${id}-life`} className="flex flex-col gap-3">
      <SectionHeader id={`${id}-life`} title={t("console.lifecycle.title")} />
      {/* Beside its controls, the deadline a cancel beats (also on Overview). */}
      {purging ? (
        <Notice tone="warning">{t("console.lifecycle.purging")}</Notice>
      ) : (
        <DeletionNotice tenant={tenant} />
      )}
      {scheduled ? (
        <p className="text-sm text-fg-secondary">
          {t("console.lifecycle.idPrefix")}{" "}
          <span className="font-mono text-fg tabular-nums">{tenantIdPrefix(tenant.id)}</span>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {available.map((a) => (
          <Button
            key={a}
            variant={a === "delete" ? "destructive" : "outline"}
            onClick={() => setAction(a)}
          >
            {t(`console.lifecycle.${a}`)}
          </Button>
        ))}
        {scheduled ? (
          <Button
            variant="destructive"
            onClick={(e) => {
              opener.current = e.currentTarget;
              setExpediting(true);
            }}
          >
            {t("console.lifecycle.expedite")}
          </Button>
        ) : null}
      </div>
      {action ? (
        <LifecycleDialog tenant={tenant} action={action} onClose={() => setAction(null)} />
      ) : null}
      {expediting ? (
        <ExpediteDeletion
          tenant={tenant}
          onClose={() => setExpediting(false)}
          returnFocus={() => opener.current}
        />
      ) : null}
    </section>
  );
}

/**
 * "Delete now": the danger dialog (what is lost, the exact name and the ID prefix, both
 * re-checked by the API), then the step-up when the last one is older than 5 minutes; the
 * purge request runs again once the code is verified.
 */
function ExpediteDeletion(props: {
  tenant: OperatorTenantDetail;
  onClose: () => void;
  returnFocus: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const stepUp = useSecureAction();
  const [confirmName, setConfirmName] = useState("");
  const [confirmId, setConfirmId] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const { tenant } = props;
  const ready =
    confirmName === tenant.name && confirmId.trim().toLowerCase() === tenantIdPrefix(tenant.id);
  const { calls, hasPasskey } = useOperatorStepUp();
  // A refusal for want of a fresh code goes back to `secure`, which asks for one and runs this
  // again; any other failure stays in the dialog.
  const expedite = async () => {
    setBusy(true);
    setError(null);
    try {
      await ok(
        await api.api.operator.tenants[":tenantId"].deletion.expedite.$post(
          { param: { tenantId: tenant.id }, json: { confirmName, confirmId: confirmId.trim() } },
          headers,
        ),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenant.id) }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenants }),
      ]);
      props.onClose();
    } catch (e) {
      if (needsReauth(e)) {
        throw e;
      }
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  const run = () =>
    stepUp.secure(expedite).catch((e: unknown) => {
      setError(e instanceof Error ? e : new Error(String(e)));
    });
  return (
    <>
      <AppDialog
        open={!stepUp.asking}
        title={t("console.lifecycle.expediteTitle", { name: tenant.name })}
        description={t("console.lifecycle.expediteBody", {
          date: tenant.deletionDueAt ? formatDateTime(tenant.deletionDueAt) : "",
        })}
        onClose={props.onClose}
        returnFocus={props.returnFocus}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready && !busy) {
              void run();
            }
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-name`}>
              {t("console.lifecycle.typeName", { name: tenant.name })}
            </Label>
            <Input
              id={`${id}-name`}
              autoComplete="off"
              value={confirmName}
              onChange={(e) => setConfirmName(e.currentTarget.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-id`}>
              {t("console.lifecycle.typeId", { prefix: tenantIdPrefix(tenant.id) })}
            </Label>
            <Input
              id={`${id}-id`}
              autoComplete="off"
              spellCheck={false}
              maxLength={TENANT_ID_PREFIX_LENGTH}
              className="font-mono"
              value={confirmId}
              onChange={(e) => setConfirmId(e.currentTarget.value)}
            />
          </div>
          {error ? <Notice tone="danger">{consoleRefusal(error)}</Notice> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={props.onClose}>
              {t("app.cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!ready || busy}>
              {t("console.lifecycle.expediteConfirm")}
            </Button>
          </div>
        </form>
      </AppDialog>
      <StepUpFrame
        open={stepUp.asking}
        onClose={stepUp.cancel}
        description={t("console.lifecycle.expediteStepUp")}
        returnFocus={props.returnFocus}
      >
        <StepUpForm
          calls={calls}
          offerPasskey={hasPasskey}
          passkeyFirst
          onClose={stepUp.cancel}
          onDone={() => {
            const action = stepUp.take();
            if (action) {
              void stepUp.secure(action).catch((e: unknown) => {
                setError(e instanceof Error ? e : new Error(String(e)));
              });
            }
          }}
        />
      </StepUpFrame>
    </>
  );
}

function LifecycleDialog(props: {
  tenant: OperatorTenantDetail;
  action: LifecycleAction;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [confirmName, setConfirmName] = useState("");
  const param = { tenantId: props.tenant.id };
  const tenants = api.api.operator.tenants[":tenantId"];
  const run = useMutation({
    mutationFn: async () => {
      switch (props.action) {
        case "suspend":
          return ok(await tenants.suspend.$post({ param }, headers));
        case "reactivate":
          return ok(await tenants.reactivate.$post({ param }, headers));
        case "delete":
          return ok(await tenants.deletion.$post({ param, json: { confirmName } }, headers));
        case "cancel":
          return ok(await tenants.deletion.$delete({ param }, headers));
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(props.tenant.id) }),
        queryClient.invalidateQueries({ queryKey: consoleKeys.tenants }),
      ]);
      props.onClose();
    },
  });
  const deleting = props.action === "delete";
  const ready = !deleting || confirmName === props.tenant.name;
  return (
    <AppDialog
      open
      title={t(`console.lifecycle.${props.action}Title`, { name: props.tenant.name })}
      description={t(`console.lifecycle.${props.action}Body`)}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !run.isPending) {
            run.mutate();
          }
        }}
      >
        {deleting ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-name`}>
              {t("console.lifecycle.typeName", { name: props.tenant.name })}
            </Label>
            <Input
              id={`${id}-name`}
              autoComplete="off"
              value={confirmName}
              onChange={(e) => setConfirmName(e.currentTarget.value)}
            />
          </div>
        ) : null}
        {run.error ? <Notice tone="danger">{consoleRefusal(run.error)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button
            type="submit"
            variant={deleting || props.action === "suspend" ? "destructive" : "default"}
            disabled={!ready || run.isPending}
          >
            {t(`console.lifecycle.${props.action}Confirm`)}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
